import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { AlertCircle, Save, Unlink, Shield, ExternalLink } from "lucide-react";

export default function IBKRPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    gateway_url: "",
    account_id: "",
    environment: "paper",
    client_id: "",
  });

  const load = () => api.get("/broker/ibkr/status").then(({ data }) => setStatus(data));

  useEffect(() => {
    load().catch(() => setStatus({ configured: false }));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/broker/ibkr/connect", {
        gateway_url: form.gateway_url.trim(),
        account_id: form.account_id.trim(),
        environment: form.environment,
        client_id: form.client_id.trim() || null,
      });
      setStatus(data);
      toast.success("IBKR configuration saved (encrypted at rest)");
      setForm({ ...form, account_id: "", client_id: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    await api.post("/broker/ibkr/disconnect").catch(() => {});
    toast.success("Disconnected");
    setStatus({ configured: false });
  };

  if (status === null) return <p className="text-sm text-gray-500">Loading IBKR…</p>;

  return (
    <div className="space-y-6" data-testid="ibkr-root">
      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="ibkr-notice-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-500 shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-display font-semibold text-lg">Gateway required — MVP stub</p>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              Interactive Brokers doesn&apos;t offer a pure cloud REST API. Live trading requires a{" "}
              <a
                href="https://www.interactivebrokers.com/en/trading/ib-api.php"
                target="_blank"
                rel="noreferrer"
                className="text-black underline inline-flex items-center gap-1"
              >
                local IBKR Gateway <ExternalLink size={10} />
              </a>{" "}
              (TWS or Client Portal Gateway) running on your machine. Because TradePilot lives in the cloud, we can&apos;t
              reach that gateway directly — <strong>live IBKR order submission is disabled</strong>.
            </p>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">
              You can still save your gateway configuration below. It&apos;s Fernet-encrypted at rest and never sent back to
              the browser. We&apos;ll unlock live sync once IBKR&apos;s OAuth-based Web API integration is available.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Badge variant="secondary" className="text-[10px]" data-testid="ibkr-cap-local">
                local_gateway_required
              </Badge>
              <Badge variant="secondary" className="text-[10px]">paper_trading (design)</Badge>
              <Badge className="bg-gray-100 text-gray-700 text-[10px]" data-testid="ibkr-cap-live-off">
                live_trading disabled
              </Badge>
              <Badge className="bg-gray-100 text-gray-700 text-[10px]">cloud_auth off</Badge>
            </div>
          </div>
        </div>
      </Card>

      {status.configured && (
        <Card className="border border-gray-200 rounded-2xl p-6" data-testid="ibkr-current-config">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Saved configuration</p>
              <p className="text-sm mt-2">
                <span className="text-gray-500">Gateway:</span>{" "}
                <span className="font-mono" data-testid="ibkr-gateway-url">{status.gateway_url}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-500">Environment:</span>{" "}
                <span className="font-mono" data-testid="ibkr-environment">{status.environment}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-500">Account ID:</span>{" "}
                <span className="font-mono" data-testid="ibkr-account-masked">{status.account_id_masked || "•"}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-500">Client ID:</span>{" "}
                <span className="font-mono">{status.client_id_set ? "•••• (set)" : "not set"}</span>
              </p>
              <p className="text-xs text-gray-400 mt-3">
                Saved {status.last_configured_at ? new Date(status.last_configured_at).toLocaleString() : "just now"} · transport disabled
              </p>
            </div>
            <Button
              data-testid="ibkr-disconnect-btn"
              variant="outline"
              onClick={disconnect}
              className="rounded-full border-gray-200"
            >
              <Unlink size={12} className="mr-2" /> Remove configuration
            </Button>
          </div>
        </Card>
      )}

      <Card className="border border-gray-200 rounded-2xl p-6 max-w-2xl" data-testid="ibkr-connect-card">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={16} className="text-gain" />
          <p className="text-sm font-medium">Credentials are Fernet-encrypted at rest. Never logged.</p>
        </div>
        <form onSubmit={save} className="space-y-4" data-testid="ibkr-form">
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Gateway URL</Label>
            <Input
              data-testid="ibkr-gateway-url-input"
              required
              value={form.gateway_url}
              onChange={(e) => setForm({ ...form, gateway_url: e.target.value })}
              placeholder="https://your-ibkr-proxy.example.com"
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Public HTTPS URL of your IBKR reverse proxy. Loopback / private IPs are rejected (SSRF guard).
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Account ID</Label>
              <Input
                data-testid="ibkr-account-id-input"
                required
                value={form.account_id}
                onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                placeholder="DU1234567 (paper) or U1234567 (live)"
                className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">Environment</Label>
              <div className="grid grid-cols-2 gap-1 mt-2 rounded-full bg-gray-100 p-1">
                {["paper", "live"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`ibkr-env-${k}`}
                    onClick={() => setForm({ ...form, environment: k })}
                    className={`rounded-full text-xs font-medium py-1.5 ${
                      form.environment === k ? "bg-white text-black shadow-sm" : "text-gray-600"
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">
              Client ID <span className="text-gray-400 font-normal">(optional)</span>
            </Label>
            <Input
              data-testid="ibkr-client-id-input"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              placeholder="1"
              className="h-11 rounded-xl bg-gray-50 border-gray-200 focus:ring-brand focus:border-brand mt-2 font-mono"
            />
          </div>
          <Button
            data-testid="ibkr-save-btn"
            type="submit"
            disabled={busy}
            className="rounded-full bg-brand text-white hover:bg-brand-deep h-11 px-6"
          >
            <Save size={14} className="mr-2" /> {busy ? "Saving…" : "Save configuration"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
